import { applyMineScoping } from '@/lib/meetings/mineScopingFilters';
// Import directly from calendarDay (not the `@/lib/time` barrel): the barrel
// also re-exports useOrgTimeFormat, which imports OrgContext.tsx. OrgContext.tsx
// reaches this module transitively (TeamProvider -> bootRequests ->
// prefetch/entries/dashboard -> here), so importing the barrel would close an
// import cycle back into OrgContext.tsx — which breaks `vi.mock('@/contexts/
// OrgContext', async importOriginal => ...)` partial mocks in unrelated tests
// (the circular resolution binds consumers to the real, unmocked module).
import {
  addCalendarDaysToYmd,
  startOfCalendarDayIn,
  todayIn,
} from '@/lib/time/calendarDay';
import type {
  CrmFilter,
  EntityIdFilter,
  FilterGroup,
  PropertyFilter,
} from '@/types/crm';

/**
 * Start of the user's calendar day in `timeZone`, and of the next one.
 *
 * Single source of truth for what "today" means on the dashboard — both the
 * meetings range (datetime instants) and the reminders range (date-only
 * strings) derive from these bounds, so the two queries can never disagree
 * about which day they cover.
 */
export function getLocalDayBounds(timeZone: string): {
  todayStart: Date;
  tomorrowStart: Date;
} {
  const todayStr = todayIn(timeZone);
  // `addCalendarDaysToYmd` normalizes the +1-day rollover as civil arithmetic
  // first; `startOfCalendarDayIn` then resolves each day to its real local
  // midnight instant (DST-correct).
  return {
    todayStart: startOfCalendarDayIn(todayStr, timeZone),
    tomorrowStart: startOfCalendarDayIn(
      addCalendarDaysToYmd(todayStr, 1),
      timeZone
    ),
  };
}

/**
 * Calculate a 1-day date range covering the given (profile) timezone's today,
 * emitted as UTC-instant ISO strings (stable for query keys; unambiguous for
 * the backend's datetime comparison on ahoy_start_time).
 *
 * Profile timezone — not UTC — because the panel says "What's happening
 * today": a user in San Francisco at 5pm is already on tomorrow's UTC day,
 * and a UTC range would show them the wrong meetings. This matches the
 * reminders range, which has always used the same day.
 *
 * Not memoized because Date calculations are cheap and this ensures
 * the filter updates correctly if the dashboard stays open past midnight.
 */
export function getDashboardDateRange(timeZone: string): {
  rangeStartISO: string;
  rangeEndISO: string;
} {
  const { todayStart, tomorrowStart } = getLocalDayBounds(timeZone);
  return {
    rangeStartISO: todayStart.toISOString(),
    rangeEndISO: tomorrowStart.toISOString(),
  };
}

/**
 * Build filter groups for today's meetings.
 *
 * Mirrors the backend filter shape from apps/quickfetch/views.py::_fetch_meetings.
 *
 * When userEntityId is null (strict equality — mirrors backend
 * `user_entity_id_str is not None`), returns only one group:
 *   1. Date + cancelled + hidden-set filters AND ahoy_meeting_to_integration in [integrationEntityIds]
 *
 * When userEntityId is not null, two OR-groups are returned:
 *   1. Date + cancelled + hidden-set filters AND ahoy_meeting_to_integration in [integrationEntityIds]
 *   2. Date + cancelled + hidden-set filters AND ahoy_meeting_to_user in [userEntityId]
 *
 * Both arms are stated positively (no isnull). Group 1 is the only path for
 * solo calendar blocks (focus time, lunch) which carry no ahoy_meeting_to_user
 * edge. Group 2 covers meetings synced by a colleague's calendar, Ahoy
 * bookings, and Recall sessions — anything that stamps ahoy_meeting_to_user
 * for this user but may not carry this user's integration. Empty
 * integrationEntityIds passes through: backend `in []` matches nothing, so
 * only Group 2 (when present) contributes results.
 *
 * hiddenSeriesIds/hiddenMeetingEntityIds come from the per-user hidden-meeting
 * store (apps/meetings/api/hidden_meeting_api.py) and are applied regardless
 * of userEntityId — the backend resolves them from the authenticated user, not
 * from the CRM user entity. Base filter indices (mirrors backend base_filters
 * in _fetch_meetings):
 *   0: ahoy_start_time gte
 *   1: ahoy_start_time lt
 *   2: ahoy_status ne cancelled
 *   3 (optional): ahoy_recurring_series_id notin hiddenSeriesIds (omitted when empty)
 *   4 (optional): entity_id notin hiddenMeetingEntityIds (omitted when empty)
 * Per-group association filters follow after the base filters in each group.
 *
 * Filter ordering within each group is binding — the React-Query cache key
 * includes the JSON-stringified filter_groups and the backend _fetch_meetings
 * must emit identical structure.
 *
 * Do not reorder the filters below without a corresponding change to
 * apps/quickfetch/views.py::_fetch_meetings — the array order must stay
 * byte-identical to the backend's filter construction for cache-key parity;
 * reordering either side alone silently breaks prefetch/live-query cache hits.
 */
export function buildTodayMeetingsFilters(
  rangeStartISO: string,
  rangeEndISO: string,
  integrationEntityIds: string[],
  userEntityId: string | null,
  hiddenSeriesIds: string[],
  hiddenMeetingEntityIds: string[]
): FilterGroup[] {
  const baseFilters: CrmFilter[] = [
    { property: 'ahoy_start_time', operator: 'gte', value: rangeStartISO },
    { property: 'ahoy_start_time', operator: 'lt', value: rangeEndISO },
    { property: 'ahoy_status', operator: 'ne', value: 'cancelled' },
  ];

  // Sort defensively at the function boundary so equivalent ID sets produce
  // identical React-Query cache keys regardless of caller. Matches the
  // backend's sort in apps/quickfetch/views.py:_fetch_meetings, ensuring
  // prefetch and live-query keys hash identically. (Integration ids are sorted
  // by buildMineScopingArms, which owns that arm.)
  const sortedHiddenSeriesIds = [...hiddenSeriesIds].sort();
  const sortedHiddenMeetingEntityIds = [...hiddenMeetingEntityIds].sort();

  if (sortedHiddenSeriesIds.length > 0) {
    baseFilters.push({
      property: 'ahoy_recurring_series_id',
      operator: 'notin',
      value: sortedHiddenSeriesIds,
    });
  }
  if (sortedHiddenMeetingEntityIds.length > 0) {
    baseFilters.push({
      subject: 'entity_id',
      operator: 'notin',
      value: sortedHiddenMeetingEntityIds,
    } as EntityIdFilter);
  }

  // Scope to meetings that are mine, using the shared definition the calendar
  // also builds from — see lib/meetings/mineScopingFilters.ts for why both arms
  // are stated positively rather than testing for an absent integration edge.
  return applyMineScoping(
    [{ filters: baseFilters }],
    integrationEntityIds,
    userEntityId
  );
}

/**
 * Today's reminder date range as date-only (YYYY-MM-DD) strings in the given
 * (profile) timezone.
 *
 * Reminders filter on `ahoy_start_date` — a date-only field — so this emits
 * calendar dates rather than datetime instants. Derived from the same
 * timezone's today as the meetings range, so meetings and reminders always
 * agree on which day "today" is. The dashboard hook (useCalendarEvents) and
 * the prefetch entry both call this so the React-Query cache key (which
 * embeds these strings) matches exactly.
 */
export function getDashboardReminderDateRange(timeZone: string): {
  todayDateOnly: string;
  tomorrowDateOnly: string;
} {
  const todayDateOnly = todayIn(timeZone);
  return {
    todayDateOnly,
    tomorrowDateOnly: addCalendarDaysToYmd(todayDateOnly, 1),
  };
}

/**
 * Build filter groups for today's reminders: a single group bounding
 * `ahoy_start_date` to [todayDateOnly, tomorrowDateOnly). Mirrors the backend
 * `_fetch_reminders` filter shape (apps/quickfetch/views.py). Shared between the
 * dashboard hook and the prefetch entry so the cache key cannot drift.
 *
 * When userEntityId is null (mirrors backend `user_entity_id_str is not None`),
 * no dismiss filter is applied (fail-open). When non-null, appends a
 * `ahoy_dismissed_by_users ne <userEntityId>` filter so only reminders not yet
 * dismissed by this user are returned. The React-Query cache key depends on
 * userEntityId (like meetings), so prefetch and live-query keys must match.
 *
 * Do not reorder the filters below without a corresponding change to
 * apps/quickfetch/views.py::_fetch_reminders — the array order must stay
 * byte-identical to the backend's filter construction for cache-key parity;
 * reordering either side alone silently breaks prefetch/live-query cache hits.
 */
export function buildTodayReminderFilters(
  todayDateOnly: string,
  tomorrowDateOnly: string,
  userEntityId: string | null
): FilterGroup[] {
  const filters: PropertyFilter[] = [
    { property: 'ahoy_start_date', operator: 'gte', value: todayDateOnly },
    { property: 'ahoy_start_date', operator: 'lt', value: tomorrowDateOnly },
  ];

  if (userEntityId !== null) {
    filters.push({
      property: 'ahoy_dismissed_by_users',
      operator: 'ne',
      value: userEntityId,
    });
  }

  return [{ filters }];
}

/**
 * Build filter groups for today's tasks (ahoy_task).
 *
 * Tasks carry their due date on one of two properties depending on whether a
 * time-of-day was set — `ahoy_datetime` (a datetime instant) or `ahoy_date`
 * (a date-only string), mirroring the eventMapper.ts due-date ladder for
 * tasks. Because a task has exactly one of the two properties set, this
 * returns two OR-groups rather than a single combined group:
 *   Group A: ahoy_datetime gte todayStartISO / lt tomorrowStartISO
 *   Group B: ahoy_date gte todayDateOnly / lt tomorrowDateOnly
 *
 * The instant bounds come from getDashboardDateRange() and the date-only
 * bounds from getDashboardReminderDateRange() — both derive from the same
 * getLocalDayBounds(), so the two groups can never disagree about which day
 * "today" is.
 */
export function buildTodayTaskFilters(
  todayDateOnly: string,
  tomorrowDateOnly: string,
  todayStartISO: string,
  tomorrowStartISO: string
): FilterGroup[] {
  const datetimeGroup: FilterGroup = {
    filters: [
      { property: 'ahoy_datetime', operator: 'gte', value: todayStartISO },
      { property: 'ahoy_datetime', operator: 'lt', value: tomorrowStartISO },
    ],
  };

  const dateOnlyGroup: FilterGroup = {
    filters: [
      { property: 'ahoy_date', operator: 'gte', value: todayDateOnly },
      { property: 'ahoy_date', operator: 'lt', value: tomorrowDateOnly },
    ],
  };

  return [datetimeGroup, dateOnlyGroup];
}
